/**
 * Card display-state mapping for the render contract.
 *
 * One place derives, from a server-enriched `OwnedCardTile`, the status-chip
 * vocabulary and the tile's Play affordance. Keeping it here means CardTile,
 * CardRow and the catalog modal all read the same truth instead of each
 * re-deriving "is this in hand / played / expired".
 *
 * It also exports the single JS mirror of the rarity palette — `RARITY_HEX`
 * and the reveal `RARITY_BURST_COLOR` — so `CardRevealOverlay.tsx` imports the
 * same values the CSS tokens hold (amendment 10) instead of hard-coding rgba
 * literals.
 */
import type {
  CardRarity,
  OwnedCardTile,
  Playability,
  TileEffectState,
} from "../types/cardTypes";

/**
 * The rarity palette in JS, mirroring `--rarity-*` in styles/cardTokens.css.
 * The CSS is the source of truth for surfaces; this is for the handful of
 * canvas/inline-animation spots (the reveal sparkle burst) that cannot read a
 * CSS variable at construction time.
 */
export const RARITY_HEX: Record<CardRarity, string> = {
  common: "#94a3b8",
  rare: "#0ea5e9",
  epic: "#a855f7",
  legendary: "#f59e0b",
};

/** Reveal sparkle-burst colour per rarity — the rarity token at high alpha. */
export const RARITY_BURST_COLOR: Record<CardRarity, string> = {
  common: "rgba(148, 163, 184, 0.9)",
  rare: "rgba(14, 165, 233, 0.95)",
  epic: "rgba(168, 85, 247, 0.95)",
  legendary: "rgba(245, 158, 11, 0.95)",
};

/** Chip vocabulary — the label shown on a card's status chip. */
export type CardChipKind =
  | "in_hand"
  | "active"
  | "armed"
  | "played"
  | "expired"
  | "owned"
  | "game_card";

export const CHIP_LABEL: Record<CardChipKind, string> = {
  in_hand: "In hand",
  active: "Active",
  armed: "Armed",
  played: "Played",
  expired: "Expired",
  owned: "Owned",
  game_card: "Game card",
};

/**
 * Whether a tile's definition is a non-playable "game card" (agent-influence
 * role cards leak into /my-cards; they have no play semantics and belong in
 * history, never the hand).
 */
export function isGameCard(tile: OwnedCardTile): boolean {
  const effectType = tile.definition?.effect?.type;
  return effectType === "agent_influence";
}

/**
 * The chip kind for a tile in the HAND grid — an in_hand copy exists. `armed`
 * for an anchored effect that is played-and-pending; otherwise `in_hand`.
 */
export function handChipKind(tile: OwnedCardTile): CardChipKind {
  if (tile.effect?.state === "pending") return "armed";
  return "in_hand";
}

/**
 * The chip kind for a tile in HISTORY — no in_hand copy. Distinguishes a live
 * played effect (`active`/`armed`) from a finished one (`played`), an
 * expired-only tile (`expired`, never "owned"), and the game-card case.
 */
export function historyChipKind(tile: OwnedCardTile): CardChipKind {
  if (isGameCard(tile)) return "game_card";
  const eff = tile.effect;
  if (eff && eff.state === "active") return "active";
  if (eff && eff.state === "pending") return "armed";
  if (tile.playedCount > 0) return "played";
  if (tile.expiredCount > 0) return "expired";
  return "owned";
}

/**
 * The Play affordance a tile should render, from its `playability`.
 *
 *  - `ready`        → an enabled "Play" button.
 *  - `needs_target` → an enabled "Play" button (target picked in the modal).
 *  - `needs_selection` → playable once the player picks (Reclamation picks up
 *                     to `maxSelections` of their used emojis; the AOTD hand
 *                     collects them and posts `emojis` with the play).
 *  - `play_before`  → an enabled button with a "play before <deadline>" hint.
 *  - `blocked`      → a disabled button with the reason.
 *  - null           → no button (nothing in hand to play).
 */
export type PlayAffordance =
  | { kind: "ready" }
  | { kind: "needs_target"; pool: Playability["pool"] }
  | {
      kind: "needs_selection";
      pool: Playability["selectionPool"];
      max: number;
    }
  | { kind: "play_before"; deadline: string | undefined }
  | { kind: "blocked"; code: string | undefined; until: string | undefined }
  | { kind: "none" };

export function playAffordance(playability: Playability | null): PlayAffordance {
  if (!playability) return { kind: "none" };
  switch (playability.state) {
    case "ready":
      return { kind: "ready" };
    case "needs_target":
      return { kind: "needs_target", pool: playability.pool };
    case "needs_selection":
      return {
        kind: "needs_selection",
        pool: playability.selectionPool,
        max: playability.maxSelections ?? 10,
      };
    case "play_before":
      return { kind: "play_before", deadline: playability.deadline };
    case "blocked":
      return { kind: "blocked", code: playability.code, until: playability.until };
    default:
      return { kind: "none" };
  }
}

/**
 * Whether a playability state can be played now: `ready`, `needs_target` and
 * `needs_selection` (the picker runs first). `play_before` is playable too but
 * kept apart, as before, because it carries a deadline.
 */
export function isPlayableState(state: Playability["state"] | undefined): boolean {
  return state === "ready" || state === "needs_target" || state === "needs_selection";
}

/**
 * Blocked codes from the AOTD card hand (modules/aotd/quiz/CONTRACT.md):
 * cards play only from the day's unlock to unlock + 45 min, and the quiz and
 * Reclamation cards have their own gates. Short labels for a disabled button;
 * `playErrors.ts` has the sentences.
 */
export const HAND_BLOCKED_LABEL: Record<string, string> = {
  outside_play_window: "Plays at the unlock",
  quiz_not_open: "No guessing open",
  not_quiz_player: "Not in today's guessing",
  second_guess_used: "Already used today",
  no_used_emojis: "No used emojis yet",
  hand_over_limit: "Pick your 4 first",
};

/** Whether a tile currently has a copy to show in the HAND section. */
export function isInHand(tile: OwnedCardTile): boolean {
  return tile.inHandCount > 0 && !isGameCard(tile);
}

/**
 * Sort comparator for the in-hand grid: playable-first (ready/needs_target/needs_selection),
 * then by soonest hand-expiry ascending, then by name. Read directly off the
 * server-computed `playability`, so a Sprint Lead sitting past its scheduled
 * time never sorts ahead of a genuinely playable card.
 */
export function comparePlayableFirst(a: OwnedCardTile, b: OwnedCardTile): number {
  const rank = (t: OwnedCardTile): number =>
    isPlayableState(t.playability?.state) ? 0 : 1;
  const byPlayable = rank(a) - rank(b);
  if (byPlayable !== 0) return byPlayable;

  const ax = a.soonestUnplayedExpiresAt
    ? new Date(a.soonestUnplayedExpiresAt).getTime()
    : Number.POSITIVE_INFINITY;
  const bx = b.soonestUnplayedExpiresAt
    ? new Date(b.soonestUnplayedExpiresAt).getTime()
    : Number.POSITIVE_INFINITY;
  if (ax !== bx) return ax - bx;

  return (a.definition?.name || "").localeCompare(b.definition?.name || "");
}

/** Whether a tile belongs in the collapsed History section (nothing in hand). */
export function isHistory(tile: OwnedCardTile): boolean {
  if (isGameCard(tile)) return true;
  return tile.inHandCount === 0;
}

/** Re-export for callers that want the raw effect state without importing the type module. */
export type { TileEffectState };
